import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageSeo from "../components/PageSeo";
import { useApp } from "../context/AppContext";
import { INDIAN_STATES, getDistrictsForState } from "../data/indianStates";
import { fetchShowrooms, isShowroomOpen } from "../lib/showrooms";
import "./Showrooms.css";

const brands = ["all", "TVS", "Honda", "Hero", "Royal Enfield", "Yamaha", "Bajaj", "KTM", "Suzuki", "BMW"];

export default function ShowroomDirectory() {
  const { user } = useApp();
  const { stateSlug, citySlug } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [state, setState] = useState("all");
  const [city, setCity] = useState("all");
  const [brand, setBrand] = useState("all");
  const [rating, setRating] = useState("all");
  const [verified, setVerified] = useState(false);
  const [premium, setPremium] = useState(false);
  const [openNow, setOpenNow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showrooms, setShowrooms] = useState([]);
  const [count, setCount] = useState(0);
  const availableCities = useMemo(() => {
    if (state === "all") return [];
    return [...new Set(getDistrictsForState(state))];
  }, [state]);

  useEffect(() => {
    if (!stateSlug) return;
    const normalizedState = INDIAN_STATES.find(
      (item) => item.toLowerCase().replace(/[^a-z0-9]+/g, "-") === stateSlug.toLowerCase()
    );
    if (normalizedState) setState(normalizedState);
  }, [stateSlug]);

  useEffect(() => {
    if (!citySlug || state === "all") return;
    const normalizedCity = getDistrictsForState(state).find(
      (item) => item.toLowerCase().replace(/[^a-z0-9]+/g, "-") === citySlug.toLowerCase()
    );
    if (normalizedCity) setCity(normalizedCity);
  }, [citySlug, state]);

  const pageTitle =
    state !== "all"
      ? city !== "all"
        ? `Bike Showrooms in ${city}, ${state} | MotoHub India`
        : `Bike Showrooms in ${state} | MotoHub India`
      : "Bike Showroom Directory | MotoHub India";
  const pageDescription =
    state !== "all"
      ? city !== "all"
        ? `Explore verified bike showrooms in ${city}, ${state}. Compare brands, ratings, services, and open listings on MotoHub India.`
        : `Explore bike showrooms across ${state}. Compare brands, ratings, services, and open listings on MotoHub India.`
      : "Search bike showrooms across India by brand, city, state, rating, verified status, premium listing, open now, and services.";

  const schema = useMemo(() => {
    const items = showrooms.slice(0, 20).map((showroom, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: showroom.showroom_name,
      url: typeof window !== "undefined" ? `${window.location.origin}/showroom/${showroom.slug}` : `/showroom/${showroom.slug}`,
    }));
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageTitle,
      description: pageDescription,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: items,
      },
    };
  }, [pageTitle, pageDescription, showrooms]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const { data, count: total } = await fetchShowrooms({
        search,
        brand,
        city,
        state,
        rating,
        verified,
        premium,
        openNow,
        limit: 24,
      });
      if (!alive) return;
      setShowrooms(data || []);
      setCount(total || 0);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [search, brand, city, state, rating, verified, premium, openNow]);

  const locationLinks = [
    { label: "Maharashtra", path: "/showrooms/maharashtra" },
    { label: "Pune", path: "/showrooms/maharashtra/pune" },
    { label: "Mumbai", path: "/showrooms/maharashtra/mumbai" },
    { label: "Karnataka", path: "/showrooms/karnataka" },
    { label: "Bengaluru", path: "/showrooms/karnataka/bengaluru" },
    { label: "Tamil Nadu", path: "/showrooms/tamil-nadu" },
    { label: "Chennai", path: "/showrooms/tamil-nadu/chennai" },
  ];

  return (
    <div className="showrooms-page">
      <PageSeo
        title={pageTitle}
        description={pageDescription}
        path={state !== "all" ? city !== "all" ? `/showrooms/${stateSlug || state.toLowerCase().replace(/[^a-z0-9]+/g, "-")}/${citySlug || city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : `/showrooms/${stateSlug || state.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "/showrooms"}
        schema={schema}
      />
      <div className="showrooms-hero">
        <div>
          <p className="showrooms-kicker">Bike Showroom Directory</p>
          <h1>Find bike showrooms like a business listing, built for riders.</h1>
          <p>Browse dealerships, compare service, save favorites, and open detailed showroom pages with SEO-friendly URLs.</p>
        </div>
        <div className="showrooms-actions">
          {user?.role === "showroom_owner" ? (
            <Link className="showrooms-primary-btn" to="/showroom-owner">Owner Dashboard</Link>
          ) : (
            <Link className="showrooms-primary-btn" to={user ? "/showroom-owner" : "/login"}>Create Listing</Link>
          )}
          <Link className="showrooms-secondary-btn" to="/search">Advanced Search</Link>
        </div>
      </div>

      <section className="showrooms-filters">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search showroom, area, city, brand" />
        <select value={state} onChange={(e) => { setState(e.target.value); setCity("all"); }}>
          <option value="all">All states</option>
          {INDIAN_STATES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={city} onChange={(e) => setCity(e.target.value)} disabled={state === "all"}>
          <option value="all">{state === "all" ? "Select state first" : "All cities"}</option>
          {availableCities.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
          {brands.map((b) => <option key={b} value={b}>{b === "all" ? "All brands" : b}</option>)}
        </select>
        <select value={rating} onChange={(e) => setRating(e.target.value)}>
          <option value="all">All ratings</option>
          <option value="4">4+</option>
          <option value="4.5">4.5+</option>
        </select>
        <button type="button" onClick={() => setVerified((v) => !v)} className={verified ? "active" : ""}>Verified</button>
        <button type="button" onClick={() => setPremium((v) => !v)} className={premium ? "active" : ""}>Premium</button>
        <button type="button" onClick={() => setOpenNow((v) => !v)} className={openNow ? "active" : ""}>Open now</button>
      </section>

      <div className="showrooms-meta">{count} listings</div>

      <div className="showroom-link-row" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {locationLinks.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              textDecoration: "none",
              fontSize: 13,
            }}
          >
            {item.label}
          </Link>
        ))}
      </div>

      {loading ? (
        <div className="showrooms-state">Loading showrooms...</div>
      ) : showrooms.length === 0 ? (
        <div className="showrooms-state">No showrooms found. Try a different search.</div>
      ) : (
        <div className="showrooms-grid">
          {showrooms.map((showroom) => (
            <article key={showroom.id} className="showroom-card" onClick={() => navigate(`/showroom/${showroom.slug}`)}>
              <div className="showroom-card-image" style={{ backgroundImage: showroom.cover_image ? `url(${showroom.cover_image})` : undefined }} />
              <div className="showroom-card-body">
                <div className="showroom-card-title-row">
                  <h3>{showroom.showroom_name}</h3>
                  <span>{Number(showroom.average_rating || 0).toFixed(1)}</span>
                </div>
                <p>{showroom.brand} · {showroom.city}, {showroom.state}</p>
                <div className="showroom-badges">
                  {showroom.verified ? <span>Verified</span> : null}
                  {showroom.premium ? <span>Premium</span> : null}
                  {isShowroomOpen(showroom) ? <span>Open now</span> : <span>Closed</span>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
